import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Avatar,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import { LineChart, PieChart, pieArcLabelClasses } from "@mui/x-charts";
import {
  Group,
  Home,
  Build,
  Today,
  HomeWork,
  MonetizationOn,
  HistoryRounded,
  AlignHorizontalRight,
  KeyboardDoubleArrowRightSharp,
  HomeMax,
  House,
  CalendarMonth,
  CycloneRounded,
  ViewDay,
  KeyboardDoubleArrowLeftSharp,
} from "@mui/icons-material";
import { tokens } from "../theme";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import stringToColor from "../services/stringToColor";
import { useNavigate } from "react-router-dom";
import { useUser } from "../provider/AuthProvider";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const StatCard = ({ icon, title, value, rating }) => {
  const isPositive = rating > 0;

  return (
    <Paper
      elevation={2}
      sx={{
        px: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        borderRadius: 3,
        height: "10vh",
        borderBlockEndColor: "#cbd5e1",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent={"space-evenly"}>
        <Box>{icon}</Box>
        <Box>
          <Typography variant="h7" color="text.secondary">
            {title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3" fontWeight={600} mr={2}>
              {value}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} ml={2}>
              {isPositive ? (
                <ArrowUpwardIcon fontSize="small" color="success" />
              ) : (
                <ArrowDownwardIcon fontSize="small" color="error" />
              )}
              <Typography variant="h5" color={isPositive ? "green" : "red"}>
                {Math.abs(rating)}%
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const { user } = useUser();
  const [announcements, setAnnouncements] = useState([]);
  const [paymentReminder, setPaymentReminder] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [maintenanceChart, setMaintenanceChart] = useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigator = useNavigate();
  const { i18n, t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);

        const [
          statsRes,
          announcementsRes,
          paymentReminderRes,
          notificationsRes,
          maintenanceChartRes,
        ] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/cmt/dashboard/stats-overview/${user.id}`
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/news/getAll/${user.id}`
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/cmt/dashboard/getPaymentReminder/${user.id}`
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/cmt/dashboard/getNotifications/${user.id}`
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/cmt/dashboard/getMaintenanceChart/${user.id}`
          ),
        ]);

        setStats(statsRes.data);
        setAnnouncements(announcementsRes.data);
        setPaymentReminder(paymentReminderRes.data);
        setNotifications(notificationsRes.data);
        setMaintenanceChart(maintenanceChartRes.data);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.id) {
      fetchDashboardData();
    }
  }, [user]);

  if (isLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Box className="main-container mt-4">
      <Grid container spacing={2} columns={{ xs: 2, sm: 10, md: 12 }}>
        <Grid item size={{ xs: 2, sm: 10, md: 7 }}>
          <Grid container columns={{ xs: 2, sm: 10, md: 12 }} spacing={3}>
            <Grid item size={{ xs: 1, sm: 5, md: 3 }}>
              <StatCard
                icon={<HomeWork fontSize="large" />}
                title={t("dashboard.total_units")}
                value={stats?.units.total}
                rating={stats?.units.rating}
              />
            </Grid>
            <Grid item size={{ xs: 1, sm: 5, md: 3 }}>
              <StatCard
                icon={<Group fontSize="large" />}
                title={t("dashboard.total_tenants")}
                value={stats?.tenants.total}
                rating={stats?.tenants.rating}
              />
            </Grid>
            <Grid item size={{ xs: 1, sm: 5, md: 3 }}>
              <StatCard
                icon={<Build fontSize="large" />}
                title={t("dashboard.total_maintenance")}
                value={stats?.requests.total}
                rating={stats?.requests.rating}
              />
            </Grid>
            <Grid item size={{ xs: 1, sm: 5, md: 3 }}>
              <StatCard
                icon={<MonetizationOn fontSize="large" />}
                title={t("dashboard.total_payment")}
                value={stats?.payments.total}
                rating={stats?.payments.rating}
              />
            </Grid>
            <Grid item size={{ xs: 2, sm: 10, md: 12 }}>
              <Paper
                sx={{
                  border: "1px solid",
                  borderColor: colors.blueAccent[500],
                  borderRadius: 1,
                  height: "40vh",
                  p: 2,
                }}
                elevation={3}
              >
                <Typography variant="h4">
                  {t("dashboard.maintenance_trends")}
                </Typography>
                <LineChart
                  xAxis={[
                    {
                      scaleType: "point",
                      data: maintenanceChart.map((d) => d.x),
                    },
                  ]}
                  series={Object.keys(maintenanceChart[0] || {})
                    .filter((k) => k !== "x")
                    .map((key) => ({
                      data: maintenanceChart.map((d) => d[key]),
                      label: t(`dashboard.${key}`),
                      area: true,
                      curve: "catmullRom",
                      showMark: false,
                    }))}
                  height={300}
                  grid={{ horizontal: true }}
                  sx={{
                    ".MuiLineElement-root": { strokeWidth: 1 },
                    ".MuiAreaElement-root": {
                      fill: "url(#rpm-gradient)",
                      opacity: 0.75,
                    },
                  }}
                >
                  <defs>
                    <linearGradient
                      id="rpm-gradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9" />
                      <stop
                        offset="100%"
                        stopColor="#9eb2f8ff"
                        stopOpacity="0.1"
                      />
                    </linearGradient>
                  </defs>
                </LineChart>
              </Paper>
            </Grid>
            <Grid item size={{ xs: 2, sm: 10, md: 6 }}>
              <Paper
                sx={{
                  border: "1px solid",
                  borderColor: colors.blueAccent[500],
                  borderRadius: 1,
                  height: "30vh",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  // justifyContent: "space-between",
                }}
                elevation={3}
              >
                <Typography variant="h4">{t("dashboard.reminders")}</Typography>
                <Box textAlign="end">
                  <Button
                    variant="inline"
                    endIcon={
                      theme.direction === "rtl" ? (
                        <KeyboardDoubleArrowLeftSharp />
                      ) : (
                        <KeyboardDoubleArrowRightSharp />
                      )
                    }
                    onClick={() => navigator("/financial")}
                  >
                    {t("dashboard.more")}
                  </Button>
                </Box>
                <Divider />
                <Paper sx={{ overflowY: "auto" }}>
                  <List>
                    {(Array.isArray(paymentReminder) &&
                    paymentReminder.length > 0
                      ? paymentReminder.slice(0, 3)
                      : []
                    ).map((log) => (
                      <>
                        <ListItem>
                          <ListItemIcon>
                            <Avatar>
                              <House />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <React.Fragment>
                                <Box display={"flex"} className="notranslate">
                                  <Typography variant="h5">
                                    {log.unit.building.name} - {log.unit.floor}{" "}
                                    - {log.unit.flat_number}
                                  </Typography>
                                </Box>
                              </React.Fragment>
                            }
                            secondary={
                              <React.Fragment>
                                <Box display="flex" py={1}>
                                  <Grid container spacing={2} columns={3}>
                                    <Grid item xs={6}>
                                      <Box
                                        display="flex"
                                        justifyContent="space-evenly"
                                        alignItems="center"
                                        mr={2}
                                      >
                                        <CalendarMonth fontSize="small" />
                                        <Typography
                                          sx={{
                                            color: "text.primary",
                                            display: "inline",
                                          }}
                                          variant="h7"
                                        >
                                          {new Date(
                                            log.next_due_date
                                          ).toDateString()}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Box
                                        display="flex"
                                        justifyContent="space-evenly"
                                        alignItems="center"
                                        mr={2}
                                      >
                                        <ViewDay fontSize="small" />
                                        <Typography
                                          sx={{
                                            color: "text.primary",
                                            display: "inline",
                                          }}
                                          variant="h7"
                                        >
                                          {log.reminder_day}{" "}
                                          {t("dashboard.day")}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Box
                                        display="flex"
                                        justifyContent="space-evenly"
                                        alignItems="center"
                                      >
                                        <CycloneRounded fontSize="small" />
                                        <Typography
                                          sx={{
                                            color: "text.primary",
                                            display: "inline",
                                          }}
                                          variant="h7"
                                        >
                                          {log.cycle}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" />
                      </>
                    ))}
                  </List>
                </Paper>
              </Paper>
            </Grid>
            <Grid item size={{ xs: 2, sm: 10, md: 6 }}>
              <Paper
                sx={{
                  border: "1px solid",
                  borderColor: colors.blueAccent[500],
                  borderRadius: 1,
                  height: "30vh",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
                elevation={3}
              >
                <Typography variant="h4">
                  {" "}
                  {t("dashboard.notifications")}
                </Typography>
                <Box textAlign="end">
                  <Button
                    variant="inline"
                    endIcon={
                      theme.direction === "rtl" ? (
                        <KeyboardDoubleArrowLeftSharp />
                      ) : (
                        <KeyboardDoubleArrowRightSharp />
                      )
                    }
                    onClick={() => navigator("/notification")}
                  >
                    {t("dashboard.more")}
                  </Button>
                </Box>
                <Divider />
                <Paper sx={{ overflowY: "auto" }}>
                  <List dense>
                    {notifications.length > 0 ? (
                      notifications.map((log) => (
                        <>
                          <ListItem>
                            <ListItemIcon>
                              {log.sender?.avatar_url ? (
                                <Avatar
                                  className="notranslate"
                                  alt={log?.sender?.username}
                                  src={log?.sender?.avatar_url}
                                />
                              ) : (
                                <Avatar
                                  className="notranslate"
                                  sx={{
                                    bgcolor: stringToColor(
                                      log?.sender?.FullName
                                    ),
                                  }}
                                >
                                  {log?.sender?.FullName.charAt(
                                    0
                                  ).toUpperCase()}
                                </Avatar>
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <React.Fragment>
                                  <Box display="flex">
                                    <Typography variant="h5">
                                      {log?.title}
                                    </Typography>
                                  </Box>
                                </React.Fragment>
                              }
                              secondary={
                                <React.Fragment>
                                  <Box display="flex" className="notranslate">
                                    {log.sender?.username == null ? (
                                      <Typography variant="body2">{`${t(
                                        "dashboard.system_alert"
                                      )}`}</Typography>
                                    ) : (
                                      <>
                                        <Typography variant="body2">
                                          {log.sender?.username}
                                        </Typography>
                                        <Typography variant="body2">
                                          &nbsp;{" | "}&nbsp;
                                        </Typography>
                                        <Typography variant="body2" dir="ltr">
                                          {log.sender?.phone}
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                </React.Fragment>
                              }
                            />
                          </ListItem>
                          <Divider variant="inset" />
                        </>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText primary="No data" />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid item size={{ xs: 2, sm: 5, md: 5 }}>
          <Paper
            sx={{
              border: "1px solid",
              borderColor: colors.blueAccent[500],
              borderRadius: 1,
              height: "85vh",
              p: 5,
            }}
            elevation={3}
          >
            <Typography variant="h4"> {t("dashboard.news")}</Typography>

            <div className="notranslate">
              <FullCalendar
                locale={i18n.language}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                editable={false}
                headerToolbar={{
                  left: "prev, next",
                  center: "title",
                  right: "",
                }}
                events={announcements.map((a) => {
                  const today = dayjs();
                  const start = dayjs(a.startDate);
                  const end = dayjs(a.endDate);

                  let backgroundColor = "#66bb6a"; // default to Active (green)

                  if (a.isActived) {
                    backgroundColor = "#9e9e9e"; // inactive
                  } else if (end.isBefore(today, "day")) {
                    backgroundColor = "#ef5350"; // expired
                  } else if (start.isAfter(today, "day")) {
                    backgroundColor = "#42a5f5"; // upcoming
                  }

                  return {
                    title: a.title,
                    start: a.startDate,
                    end: a.endDate,
                    backgroundColor,
                    borderColor: "transparent",
                    textColor: "#fff",
                    extendedProps: {
                      message: a.message,
                    },
                  };
                })}
                eventDidMount={(info) => {
                  info.el.style.borderRadius = "8px";
                  info.el.style.padding = "6px 8px";
                  info.el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                  info.el.style.fontWeight = "500";
                  info.el.style.fontSize = "0.875rem";
                  info.el.style.fontFamily = '"Roboto", sans-serif';

                  info.el.onmouseenter = () => {
                    info.el.style.transform = "scale(1.02)";
                    info.el.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                  };
                  info.el.onmouseleave = () => {
                    info.el.style.transform = "scale(1)";
                    info.el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                  };
                }}
              />
            </div>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
